import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    Loader2, RefreshCw, Send, Eye, CheckCircle2, MailX, Percent, Clock, BarChart3,
} from 'lucide-react';
import { analyticsAPI, organizationsAPI } from '../../lib/api';

/**
 * P_COMMUNICATION_ANALYTICS_4.0b — Read-only Analytics tab for the
 * Communications Hub.
 *
 * Constraints honoured:
 *   • No polling. No local cache. No persistence.
 *   • Filter changes auto-refresh via useEffect.
 *   • All numbers come from backend; UI never re-computes percentages
 *     or averages from raw counts. The backend guarantees no NaN.
 *   • avg_human === null  →  i18n key `analytics.notYet`.
 *   • sample_size < 10    →  show "N/A" for the avg-TTFS card.
 */

const DEFAULT_WINDOW_DAYS = 30;

function defaultRange() {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - DEFAULT_WINDOW_DAYS + 1);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return { start: fmt(start), end: fmt(today) };
}

const SAMPLE_FLOOR = 10;

const KpiCard = ({ icon: Icon, label, value, tone = 'default', testid }) => (
    <Card className="p-4 space-y-1" data-testid={testid}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon className="w-4 h-4" />
            {label}
        </div>
        <div className={`text-2xl font-semibold ${
            tone === 'seen' ? 'text-emerald-600 dark:text-emerald-400'
            : tone === 'ack' ? 'text-amber-600 dark:text-amber-400'
            : tone === 'unopened' ? 'text-rose-600 dark:text-rose-400'
            : 'text-foreground'
        }`}>{value}</div>
    </Card>
);

const Skeleton = () => (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="analytics-skeleton">
        {Array.from({ length: 7 }).map((_, i) => (
            <Card key={i} className="p-4 space-y-2 animate-pulse">
                <div className="h-3 w-20 bg-muted rounded" />
                <div className="h-6 w-16 bg-muted rounded" />
            </Card>
        ))}
    </div>
);

// Format a percent with floor guard. Backend always returns a clean
// number, but the floor rule lives in the UI.
function fmtPct(value, sampleSize, fallbackNA) {
    if (sampleSize === 0) return '0%';  // no sample, but no infinity either
    if (sampleSize < SAMPLE_FLOOR) return fallbackNA;
    return `${(value ?? 0).toFixed(1)}%`;
}

function fmtAvg(avgHuman, sampleSize, fallbackNA, notYetLabel) {
    if (sampleSize === 0) return notYetLabel;
    if (sampleSize < SAMPLE_FLOOR) return fallbackNA;
    return avgHuman ?? notYetLabel;
}

const SORT_FIELDS_WELCOME = [
    'organization', 'sent', 'seen', 'acknowledged', 'seen_pct', 'ack_pct', 'avg',
];
const SORT_FIELDS_CAMPAIGN = [
    'title', 'organization', 'sent', 'seen', 'acknowledged', 'seen_pct', 'ack_pct', 'avg',
];

function sortRows(rows, sortKey, sortDir, getOrgName) {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        let av = a[sortKey];
        let bv = b[sortKey];
        if (sortKey === 'organization') {
            av = getOrgName(a.organization_id) || '';
            bv = getOrgName(b.organization_id) || '';
        }
        if (sortKey === 'avg') {
            av = a.avg_seconds_to_first_seen ?? Infinity;
            bv = b.avg_seconds_to_first_seen ?? Infinity;
        }
        if (sortKey === 'title') {
            av = a.title || '';
            bv = b.title || '';
        }
        if (typeof av === 'string' || typeof bv === 'string') {
            return String(av).localeCompare(String(bv)) * dir;
        }
        return ((av ?? 0) - (bv ?? 0)) * dir;
    });
}

export const AnalyticsTab = () => {
    const { t } = useTranslation();
    const [range, setRange] = useState(defaultRange());
    const [orgFilter, setOrgFilter] = useState('');
    const [organizations, setOrganizations] = useState([]);
    const [summary, setSummary] = useState(null);
    const [welcome, setWelcome] = useState(null);
    const [campaigns, setCampaigns] = useState(null);
    const [loading, setLoading] = useState(true);
    const [welcomeSort, setWelcomeSort] = useState({ key: 'sent', dir: 'desc' });
    const [campSort, setCampSort] = useState({ key: 'sent', dir: 'desc' });

    // Load orgs once.
    useEffect(() => {
        organizationsAPI.list()
            .then(r => setOrganizations(r.data?.items || r.data || []))
            .catch(() => setOrganizations([]));
    }, []);

    const orgMap = useMemo(() => {
        const m = new Map();
        for (const o of organizations) m.set(o.id, o.name || o.id);
        return m;
    }, [organizations]);
    const getOrgName = useCallback((id) => orgMap.get(id) || id, [orgMap]);

    // Auto-refresh on filter changes.
    const filtersKey = `${range.start}|${range.end}|${orgFilter || ''}`;
    useEffect(() => {
        let cancelled = false;
        const params = {
            start: range.start,
            end: range.end,
            ...(orgFilter ? { organization_id: orgFilter } : {}),
        };
        setLoading(true);
        Promise.all([
            analyticsAPI.summary(params),
            analyticsAPI.welcome(params),
            analyticsAPI.campaigns({ ...params, limit: 20 }),
        ]).then(([s, w, c]) => {
            if (cancelled) return;
            setSummary(s.data);
            setWelcome(w.data);
            setCampaigns(c.data);
        }).catch(() => {
            if (cancelled) return;
            setSummary(null); setWelcome(null); setCampaigns(null);
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const combined = summary?.combined;
    const naLabel = t('analytics.na');
    const notYetLabel = t('analytics.notYet');

    const reloadNow = () => setRange({ ...range }); // re-trigger via new object ref
    // Force the effect to fire even though `range` didn't change — bump
    // a key by toggling and back. Simpler: same-value setState won't
    // fire useEffect because filtersKey string is identical. Use a
    // dedicated nonce.
    const [nonce, setNonce] = useState(0);
    useEffect(() => {
        if (nonce === 0) return;
        // Trigger refetch by re-using filtersKey effect via small delta —
        // we directly invoke the loader.
        let cancelled = false;
        const params = {
            start: range.start,
            end: range.end,
            ...(orgFilter ? { organization_id: orgFilter } : {}),
        };
        setLoading(true);
        Promise.all([
            analyticsAPI.summary(params),
            analyticsAPI.welcome(params),
            analyticsAPI.campaigns({ ...params, limit: 20 }),
        ]).then(([s, w, c]) => {
            if (cancelled) return;
            setSummary(s.data); setWelcome(w.data); setCampaigns(c.data);
        }).catch(() => {
            if (cancelled) return;
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nonce]);

    return (
        <div className="space-y-4" data-testid="analytics-tab">
            {/* Filter row */}
            <Card className="p-3 md:p-4 flex flex-col md:flex-row gap-3 md:items-end" data-testid="analytics-filters">
                <div className="flex-1 grid grid-cols-2 gap-2 md:gap-3 md:flex md:flex-row">
                    <div className="flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1">{t('analytics.filters.start')}</label>
                        <input type="date" value={range.start}
                            onChange={(e) => setRange({ ...range, start: e.target.value })}
                            className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground"
                            data-testid="analytics-filter-start" />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1">{t('analytics.filters.end')}</label>
                        <input type="date" value={range.end}
                            onChange={(e) => setRange({ ...range, end: e.target.value })}
                            className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground"
                            data-testid="analytics-filter-end" />
                    </div>
                    <div className="flex flex-col col-span-2 md:col-span-1 md:min-w-[200px]">
                        <label className="text-xs text-muted-foreground mb-1">{t('analytics.filters.organization')}</label>
                        <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}
                            className="bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground"
                            data-testid="analytics-filter-organization">
                            <option value="">{t('analytics.filters.allOrgs')}</option>
                            {organizations.map(o => (
                                <option key={o.id} value={o.id}>{o.name || o.id}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => setNonce(n => n + 1)}
                    disabled={loading} data-testid="analytics-reload">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    {t('analytics.reload')}
                </Button>
            </Card>

            {/* KPI strip */}
            {loading && !summary ? (
                <Skeleton />
            ) : !combined ? (
                <Card className="p-8 text-center text-muted-foreground" data-testid="analytics-empty">
                    {t('analytics.empty')}
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="analytics-kpi-strip">
                        <KpiCard icon={Send} label={t('analytics.kpi.sent')}
                            value={combined.sent} testid="analytics-kpi-sent" />
                        <KpiCard icon={Eye} label={t('analytics.kpi.seen')}
                            value={combined.seen} tone="seen" testid="analytics-kpi-seen" />
                        <KpiCard icon={CheckCircle2} label={t('analytics.kpi.ack')}
                            value={combined.acknowledged} tone="ack" testid="analytics-kpi-ack" />
                        <KpiCard icon={MailX} label={t('analytics.kpi.unopened')}
                            value={combined.unopened} tone="unopened" testid="analytics-kpi-unopened" />
                        <KpiCard icon={Percent} label={t('analytics.kpi.seenPct')}
                            value={fmtPct(combined.seen_pct, combined.sample_size + combined.unopened, naLabel) /* seen% uses sent as denom; show always when sent>0, never NaN */}
                            testid="analytics-kpi-seen-pct" />
                        <KpiCard icon={Percent} label={t('analytics.kpi.ackPct')}
                            value={fmtPct(combined.ack_pct, combined.sample_size + combined.unopened, naLabel)}
                            testid="analytics-kpi-ack-pct" />
                        <KpiCard icon={Clock} label={t('analytics.kpi.avgTtfs')}
                            value={fmtAvg(combined.avg_human, combined.sample_size, naLabel, notYetLabel)}
                            testid="analytics-kpi-avg" />
                    </div>

                    {/* Welcome breakdown */}
                    <Card className="p-4 space-y-3" data-testid="analytics-welcome-table">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <BarChart3 className="w-4 h-4 text-muted-foreground" />
                            {t('analytics.welcome.title')}
                        </div>
                        {welcome?.by_organization?.length ? (
                            <Table
                                rows={sortRows(
                                    welcome.by_organization.map(r => ({ ...r, organization: getOrgName(r.organization_id) })),
                                    welcomeSort.key, welcomeSort.dir, getOrgName,
                                )}
                                cols={[
                                    { k: 'organization', label: t('analytics.welcome.cols.org') },
                                    { k: 'sent', label: t('analytics.welcome.cols.sent'), num: true },
                                    { k: 'seen', label: t('analytics.welcome.cols.seen'), num: true },
                                    { k: 'acknowledged', label: t('analytics.welcome.cols.ack'), num: true },
                                    { k: 'seen_pct', label: t('analytics.welcome.cols.seenPct'), num: true,
                                      render: (r) => fmtPct(r.seen_pct, r.sent, naLabel) },
                                    { k: 'ack_pct', label: t('analytics.welcome.cols.ackPct'), num: true,
                                      render: (r) => fmtPct(r.ack_pct, r.sent, naLabel) },
                                    { k: 'avg', label: t('analytics.welcome.cols.avg'),
                                      render: (r) => fmtAvg(r.avg_human, r.sample_size, naLabel, notYetLabel) },
                                ]}
                                sortKey={welcomeSort.key} sortDir={welcomeSort.dir}
                                onSort={(k) => setWelcomeSort(s => ({
                                    key: k,
                                    dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc',
                                }))}
                                fields={SORT_FIELDS_WELCOME}
                                testid="welcome"
                            />
                        ) : (orgFilter && welcome?.totals?.sent === 0) ? (
                            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="analytics-welcome-empty">
                                {t('analytics.empty')}
                            </div>
                        ) : orgFilter ? (
                            <SingleRowFromTotals totals={welcome?.totals} orgName={getOrgName(orgFilter)} t={t} naLabel={naLabel} notYetLabel={notYetLabel} testid="welcome" />
                        ) : (
                            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="analytics-welcome-empty">
                                {t('analytics.empty')}
                            </div>
                        )}
                    </Card>

                    {/* Campaign breakdown */}
                    <Card className="p-4 space-y-3" data-testid="analytics-campaigns-table">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                            <BarChart3 className="w-4 h-4 text-muted-foreground" />
                            {t('analytics.campaigns.title')}
                        </div>
                        {campaigns?.by_campaign?.length ? (
                            <Table
                                rows={sortRows(
                                    campaigns.by_campaign.map(r => ({
                                        ...r,
                                        organization: getOrgName(r.organization_id) || '—',
                                    })),
                                    campSort.key, campSort.dir, getOrgName,
                                )}
                                cols={[
                                    { k: 'title', label: t('analytics.campaigns.cols.title') },
                                    { k: 'organization', label: t('analytics.campaigns.cols.org') },
                                    { k: 'sent', label: t('analytics.campaigns.cols.sent'), num: true },
                                    { k: 'seen', label: t('analytics.campaigns.cols.seen'), num: true },
                                    { k: 'acknowledged', label: t('analytics.campaigns.cols.ack'), num: true },
                                    { k: 'seen_pct', label: t('analytics.campaigns.cols.seenPct'), num: true,
                                      render: (r) => fmtPct(r.seen_pct, r.sent, naLabel) },
                                    { k: 'ack_pct', label: t('analytics.campaigns.cols.ackPct'), num: true,
                                      render: (r) => fmtPct(r.ack_pct, r.sent, naLabel) },
                                    { k: 'avg', label: t('analytics.campaigns.cols.avg'),
                                      render: (r) => fmtAvg(r.avg_human, r.sample_size, naLabel, notYetLabel) },
                                ]}
                                sortKey={campSort.key} sortDir={campSort.dir}
                                onSort={(k) => setCampSort(s => ({
                                    key: k,
                                    dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc',
                                }))}
                                fields={SORT_FIELDS_CAMPAIGN}
                                testid="campaigns"
                            />
                        ) : (
                            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="analytics-campaigns-empty">
                                {t('analytics.empty')}
                            </div>
                        )}
                    </Card>
                </>
            )}
        </div>
    );
};

// Inner table component.
const Table = ({ rows, cols, sortKey, sortDir, onSort, fields, testid }) => (
    <div className="overflow-x-auto" data-testid={`analytics-${testid}-table-body`}>
        <table className="w-full text-sm">
            <thead className="bg-muted/40">
                <tr className="text-xs uppercase text-muted-foreground">
                    {cols.map(c => (
                        <th key={c.k}
                            className={`px-3 py-2 font-medium ${c.num ? 'text-right' : 'text-left'} ${fields.includes(c.k) ? 'cursor-pointer select-none' : ''}`}
                            onClick={() => fields.includes(c.k) && onSort(c.k)}
                            data-testid={`analytics-${testid}-sort-${c.k}`}>
                            {c.label}
                            {sortKey === c.k ? <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span> : null}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((r, i) => (
                    <tr key={r.organization_id || r.campaign_id || i}
                        className="border-b border-border last:border-b-0 hover:bg-muted/40"
                        data-testid={`analytics-${testid}-row-${i}`}>
                        {cols.map(c => (
                            <td key={c.k} className={`px-3 py-2 ${c.num ? 'text-right' : 'text-left'} text-foreground`}>
                                {c.render ? c.render(r) : (r[c.k] ?? '—')}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// When the org filter is active we don't get a per-org rollup from the
// backend (the backend skips that work — it would just echo the totals
// back). Render a single-row table from totals so the UI still shows
// the org name + KPIs.
const SingleRowFromTotals = ({ totals, orgName, t, naLabel, notYetLabel, testid }) => {
    if (!totals) return null;
    const r = {
        organization: orgName, sent: totals.sent, seen: totals.seen,
        acknowledged: totals.acknowledged, seen_pct: totals.seen_pct,
        ack_pct: totals.ack_pct, avg_human: totals.avg_human,
        sample_size: totals.sample_size,
    };
    return (
        <div className="overflow-x-auto" data-testid={`analytics-${testid}-table-body`}>
            <table className="w-full text-sm">
                <thead className="bg-muted/40">
                    <tr className="text-xs uppercase text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">{t('analytics.welcome.cols.org')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('analytics.welcome.cols.sent')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('analytics.welcome.cols.seen')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('analytics.welcome.cols.ack')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('analytics.welcome.cols.seenPct')}</th>
                        <th className="px-3 py-2 text-right font-medium">{t('analytics.welcome.cols.ackPct')}</th>
                        <th className="px-3 py-2 text-left font-medium">{t('analytics.welcome.cols.avg')}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="px-3 py-2 text-left text-foreground">{r.organization}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.sent}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.seen}</td>
                        <td className="px-3 py-2 text-right text-foreground">{r.acknowledged}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtPct(r.seen_pct, r.sent, naLabel)}</td>
                        <td className="px-3 py-2 text-right text-foreground">{fmtPct(r.ack_pct, r.sent, naLabel)}</td>
                        <td className="px-3 py-2 text-left text-foreground">{fmtAvg(r.avg_human, r.sample_size, naLabel, notYetLabel)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default AnalyticsTab;
